/**
 * Google Roads API integration for Traffic Density demo mode
 */

/**
 * Generate realistic mock traffic density data using Google Directions API
 */
export async function generateMockTrafficDataGoogle(
	bbox: [number, number, number, number],
	timeSets: string[] = ["Mattina", "Pomeriggio", "Sera"],
): Promise<any> {
	const [minLon, minLat, maxLon, maxLat] = bbox;

	try {
		const features: any[] = [];
		const targetRoutes = 12; // Desired number of routes
		let attempts = 0;
		const maxAttempts = 50; // Avoid infinite loops

		while (features.length < targetRoutes && attempts < maxAttempts) {
			attempts++;

			// Random start and end points within bbox
			// ... (coordinates generation)
			const startLat = minLat + Math.random() * (maxLat - minLat);
			const startLon = minLon + Math.random() * (maxLon - minLon);
			const endLat = minLat + Math.random() * (maxLat - minLat);
			const endLon = minLon + Math.random() * (maxLon - minLon);

			// Use Google Directions API via nginx proxy (chiave aggiunta server-side)
			const url =
				`/proxy/google-directions/json?` +
				`origin=${startLat},${startLon}&` +
				`destination=${endLat},${endLon}&` +
				`mode=driving`;

			const response = await fetch(url);

			if (!response.ok) {
				console.warn(`[TrafficDensity] Google API error: ${response.status}`);
				continue;
			}

			const data = await response.json();

			if (data.status !== "OK" || !data.routes || data.routes.length === 0) {
				continue;
			}

			// Extract route geometry
			const route = data.routes[0];
			const legs = route.legs || [];

			legs.forEach((leg: any, legIndex: number) => {
				leg.steps?.forEach((step: any, stepIndex: number) => {
					// Decode polyline to get coordinates
					const coordinates = decodePolyline(step.polyline.points);

					if (coordinates.length < 2) return;

					// Generate mock traffic data
					const roadName =
						step.html_instructions?.replace(/<[^>]*>/g, "") ||
						`Route ${features.length + 1}`;
					const distance = step.distance?.value || 0; // meters
					const duration = step.duration?.value || 0; // seconds

					const avgSpeed =
						distance > 0 && duration > 0 ? (distance / duration) * 3.6 : 50;
					const baseDensity = avgSpeed > 80 ? 1200 : avgSpeed > 50 ? 800 : 400;
					const sampleSize = Math.floor(baseDensity + Math.random() * 600);

					// Use features.length as index for distribution
					const distributionIndex = features.length;

					features.push({
						type: "Feature",
						geometry: {
							type: "LineString",
							coordinates,
						},
						properties: {
							roadName: roadName.substring(0, 100),
							frc:
								avgSpeed > 80 ? 0 : avgSpeed > 60 ? 1 : avgSpeed > 40 ? 2 : 3,
							length: (distance / 1000).toFixed(2),
							sampleSize: sampleSize,
							averageSpeed: avgSpeed.toFixed(1),
							timeSet: timeSets[distributionIndex % timeSets.length], // Distribute among selected periods
						},
					});
				});
			});

			// Small delay to avoid rate limiting
			await new Promise((resolve) => setTimeout(resolve, 150));
		}

		console.log(
			`[TrafficDensity] Generated ${features.length} road segments from Google`,
		);

		return {
			type: "FeatureCollection",
			features: features.slice(0, 100),
		};
	} catch (error) {
		console.error("[TrafficDensity] Google API failed:", error);
		return { type: "FeatureCollection", features: [] };
	}
}

/**
 * Decode Google polyline format to coordinates
 */
function decodePolyline(encoded: string): [number, number][] {
	const coordinates: [number, number][] = [];
	let index = 0;
	let lat = 0;
	let lng = 0;

	while (index < encoded.length) {
		let b;
		let shift = 0;
		let result = 0;

		do {
			b = encoded.charCodeAt(index++) - 63;
			result |= (b & 0x1f) << shift;
			shift += 5;
		} while (b >= 0x20);

		const dlat = result & 1 ? ~(result >> 1) : result >> 1;
		lat += dlat;

		shift = 0;
		result = 0;

		do {
			b = encoded.charCodeAt(index++) - 63;
			result |= (b & 0x1f) << shift;
			shift += 5;
		} while (b >= 0x20);

		const dlng = result & 1 ? ~(result >> 1) : result >> 1;
		lng += dlng;

		coordinates.push([lng / 1e5, lat / 1e5]);
	}

	return coordinates;
}
